import React from 'react';

import { FloatingPortal } from '@floating-ui/react';
import { Check } from 'lucide-react';

import { cn } from '../../lib/utils';

import { GlassWrapper } from '../shared/GlassWrapper';
import { MenuButton } from '../shared/MenuButton';
import MenuDivider from '../shared/MenuDivider';

// remove vendor id hex patterns (vendor:product) like "(05ac:1114)"
const cleanDeviceLabel = (label) => {
  if (!label) return label;
  return label.replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)/gi, '').trim();
};

function DeviceList({ title, icon: Icon, devices, activeDeviceId, onDeviceChange }) {
  return (
    <div className="flex w-full flex-col">
      <div className="cursor-default px-2.5 py-1.5 text-2xs font-semibold text-gray-800 dark:text-neutral-200">{title}</div>
      {devices.map((device) => {
        const isActive = device.deviceId === activeDeviceId;
        return (
          <MenuButton
            key={device.deviceId}
            role="menuitemradio"
            aria-checked={isActive}
            onClick={() => onDeviceChange(device.deviceId)}
            // MenuButton wraps its children in a single span. Turning that span into the row
            // is what lets the label truncate and the check sit at the end, while the button
            // itself keeps the context menu's padding, radius and hover untouched.
            className={cn(
              '[&>span]:flex [&>span]:w-full [&>span]:items-center [&>span]:gap-2.5',
              // Scoped to this menu rather than pushed into MenuButton — the shared default
              // is what every other menu in the app hovers with.
              'hover:bg-white/90 dark:hover:bg-white/10',
              // The selected device is the one you can read at a glance, but the alternatives
              // are still a list you have to read to pick from — weight carries the
              // distinction, not a drop in legibility.
              isActive ? 'font-semibold text-black dark:text-white' : 'text-gray-700 dark:text-neutral-300',
            )}
          >
            {Icon ? <Icon size={12} aria-hidden="true" className="shrink-0" /> : null}
            <span className="min-w-0 flex-1 truncate">{cleanDeviceLabel(device.label) || device.deviceId}</span>
            {/* A filled accent disc, not a bare tick — at 12px a stroked check reads as one
                more glyph in the row rather than as the thing that marks the selection. */}
            {isActive ? (
              <span aria-hidden="true" className="flex size-4 shrink-0 items-center justify-center rounded-full bg-idle-blue text-white">
                <Check size={10} strokeWidth={1} absoluteStrokeWidth />
              </span>
            ) : null}
          </MenuButton>
        );
      })}
    </div>
  );
}

// Dumb renderer: every section comes from useMediaDevicePreferences, which is the single
// owner of device state. Used for both the camera chevron (one section) and the mic
// chevron (input + output).
//
// Wears the same shell as the right-click menu — GlassWrapper's default popover surface with
// MenuButton rows — so every menu in the app reads as one component.
export function ConferenceDeviceMenu({ sections, refs, floatingStyles, getFloatingProps }) {
  return (
    <FloatingPortal>
      <GlassWrapper
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps({ role: 'menu' })}
        className="z-(--stack-conference-bubble-media-device-menu) w-60 gap-0"
      >
        {sections.map((section, index) => (
          <React.Fragment key={section.title}>
            {index > 0 ? <MenuDivider /> : null}
            <DeviceList
              title={section.title}
              icon={section.icon}
              devices={section.devices}
              activeDeviceId={section.activeDeviceId}
              onDeviceChange={section.onChange}
            />
          </React.Fragment>
        ))}
      </GlassWrapper>
    </FloatingPortal>
  );
}
